import useReadNotification from '@/hooks/notifications/useReadNotification';
import { TNotification } from '@/types/Notification.types';
import { HStack, VStack } from '@chakra-ui/react';
import { formatDistanceToNow } from 'date-fns';
import React, { useCallback, useEffect } from 'react';
import { useInView } from 'react-intersection-observer';
import NotificationItemDisplay from './NotificationItemDisplay';
import { useNavigate } from '@tanstack/react-router';
import { NOTIFICATION_ACTIONS_DISPLAY_LABELS } from '@/constants/stringVars';
import { NOTIFICATION_READ_DELAY_MILLISECONDS } from '@/constants/numberVars';

interface Props {
  notification: TNotification;
  onViewed?: (notification: TNotification) => void;
  onCTAed?: (notification: TNotification) => void;
}

const NotificationListItem = ({ notification, onViewed, onCTAed }: Props) => {
  const navigate = useNavigate();

  const hasCTA = !!notification.action;

  const isNotificationRead = notification.isRead;

  const { mutate: readNotification } = useReadNotification(notification);

  const { ref: inViewRef, inView } = useInView({
    threshold: 0.9,
    trackVisibility: !!onViewed,
    skip: !onViewed,
    delay: 300,
  });

  useEffect(() => {
    if (!!onViewed && !isNotificationRead && !hasCTA && inView) {
      setTimeout(
        () => readNotification({ notificationId: notification._id, onSuccessCallback: onViewed }),
        NOTIFICATION_READ_DELAY_MILLISECONDS,
      );
    }
  }, [onViewed, isNotificationRead, hasCTA, inView]);

  const onCTAClick = useCallback(async () => {
    await readNotification({ notificationId: notification._id, onSuccessCallback: onCTAed });

    switch (notification.action) {
      case 'SCHEDULE_APPOINTMENT':
      case 'SCHEDULE_THERAPIST':
        navigate({
          to: '/member-care/members/$memberCareMemberId',
          params: { memberCareMemberId: notification.metadata?.userId ?? '' },
        });
        break;

      case 'SCREENER_CHECK_IN':
        navigate({ to: `/screener/${notification.metadata?.screenerSlug}` });
        break;

      case 'VIEW_PROFILE':
        navigate({
          to: '/member-care/members/$memberCareMemberId',
          params: { memberCareMemberId: notification.metadata?.userId ?? '' },
        });
        break;

      case undefined:
      default:
        return undefined;
    }
  }, [notification]);

  return (
    <VStack ref={inViewRef}>
      <NotificationItemDisplay
        title={notification.title.toUpperCase()}
        timeStamp={formatDistanceToNow(notification.createdAt, { addSuffix: true })}
        message={notification.message}
        isRead={isNotificationRead}
        CTALabel={
          notification.action
            ? NOTIFICATION_ACTIONS_DISPLAY_LABELS[
                notification.action as keyof typeof NOTIFICATION_ACTIONS_DISPLAY_LABELS
              ]
            : undefined
        }
        onCTAClick={notification.action ? onCTAClick : undefined}
      />

      <HStack height={'1px'} backgroundColor={'#33516D24'} width={'114%'} />
    </VStack>
  );
};

export default NotificationListItem;
